import { useEffect } from "react";

/**
 * Global scroll-triggered fade for every <section> on the page.
 * Applies a starting hidden state and reveals as each section enters the viewport.
 */
export function ScrollFadeSections() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const seen = new WeakSet<Element>();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("mck-section-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -60px 0px" }
    );

    const attach = (el: Element) => {
      if (seen.has(el)) return;
      if ((el as HTMLElement).dataset.noReveal !== undefined) return;
      seen.add(el);
      el.classList.add("mck-section-reveal");
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.9) {
        el.classList.add("mck-section-in");
        return;
      }
      io.observe(el);
      // Safety fallback: never leave a section permanently hidden.
      window.setTimeout(() => {
        if (!el.classList.contains("mck-section-in")) {
          el.classList.add("mck-section-in");
          io.unobserve(el);
        }
      }, 2500);
    };

    const scan = () => document.querySelectorAll("main section").forEach(attach);
    scan();

    const mo = new MutationObserver(() => scan());
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, []);

  return null;
}